<template>
    <div class="messages" ref="messages" @scroll="loadMore">
      <div class="is-flex loading"><span v-if="loading||showPreloader" class="loader"></span></div>
      <div v-for="message in conversation.messages" :key="message.id" class="message">
        <message :id="`message-${message.id}`" :message="message" :chatType="chatType" :conversationID="conversation.id" :isSupervisor="isSupervisor" :deletedUser="conversationUsers.length > 0 && conversationUsers.indexOf(message.sender.id) < 0" @toggleEdit="toggleEdit" v-on:showModal="$emit('showModal', $event)" @saveState="saveState"></message>
      </div>
    </div>
</template>

<script>

import socket from '@/libs/socket'
import debounce from 'lodash/debounce'

import auth from '@/mixins/auth'
import componentState from '@/mixins/component-state'

import Message from './Message.vue'

export default {
  props: ['conversation', 'users', 'isSupervisor', 'chatType'],
  components: {
    Message
  },
  mixins: [auth, componentState],
  data: function () {
    return {
      loading: false,
      limit: 10,
      subscribed: false,
      firstID: null,
      lastID: null,
      previousConversation: {},
      scrollHeight: 0,
      showPreloader: false
    }
  },

  mounted: function () {
    if (this.conversation.messages.length <= this.limit) {
      this.checkLoadMore()
      this.subscribe(this.conversation.id)
      this.previousConversation = this.conversation
    }
    this.$nextTick(() => {
      this.restoreState()
    })
  },

  destroyed: function () {
    if (this.subscribed) {
      this.unsubscribe(this.conversation.id)
    }
  },

  watch: {
    conversation: function (value) {
      if (this.subscribed) {
        this.unsubscribe(this.previousConversation.id)
      }
      this.subscribe(this.conversation.id)
      this.previousConversation = this.conversation
      this.$nextTick(() => {
        if (this.$route.query.messageID) {
          const messageID = this.$route.query.messageID
          const message = document.getElementById(`message-${messageID}`)
          if (message) {
            message.scrollIntoView()
          }
        }
      })
    },
    'conversation.id': function () {
      if (this.messages.length < 10) {
        this.fetchMessages()
      }
    }
  },

  updated: function () {
    if (this.messages.length > 0) {
      const firstID = this.messages[0].id
      const lastID = this.messages[this.messages.length - 1].id
      if (this.firstID !== firstID) {
        this.firstID = firstID
      }

      if (this.lastID !== lastID) {
        this.lastID = lastID
        this.scrollToBottom()
      }
    }
  },

  computed: {
    messages: function () {
      return this.conversation.messages || []
    },
    conversationUsers: function () {
      return this.users?.map((user) => { return user.id })
    },
    isFirstLoad: function () {
      return this.messages.length < 10
    },
    reachedScrollTop: function () {
      return this.$refs?.messages?.scrollTop < 40
    }
  },

  methods: {
    subscribe: function (id) {
      if (this.conversation.draft || !this.conversation.notifications) {
        return
      }
      this.subscribed = true
      socket.subscribe('conversation', id, 'message')
    },
    unsubscribe: function (id) {
      this.subscribed = false
      socket.unsubscribe('conversation', id, 'message')
    },
    fetchMessages: debounce(function () {
      if (this.loading || this.conversation.draft || this.messages === undefined || this.messages.length === 0) {
        return
      }

      let limit = this.limit
      let first = false
      if (this.messages.length === 1) {
        limit--
        first = true
      }
      const before = this.messages[0].id
      this.loading = true
      let dispatchMethod = 'getMessages'
      if (this.conversation.public) {
        dispatchMethod = 'getPublicMessages'
      }
      this.$store.dispatch(dispatchMethod, { id: this.conversation.id, limit, before }).then((response) => {
        this.loading = false
        this.showPreloader = false
        if (first) {
          this.scrollToBottom()
        } else if (this.$refs.messages.scrollTop < 40 && !this.isFirstLoad) {
          this.scrollToPreviousTop()
        }
      }, (response) => {
        this.loading = false
      })
    }, 300),
    scrollToBottom: function () {
      this.$nextTick(() => {
        if (this.$refs.messages) {
          this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight
        }
      })
    },
    scrollToPreviousTop: function () {
      this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight - this.scrollHeight
    },
    loadMore: function () {
      if (!this.conversation.hasMoreMessages || this.loading) {
        return
      }

      if (this.$refs.messages.scrollTop < 40) {
        this.showPreloader = true
        this.saveScrollHeight()
        this.fetchMessages()
      }
    },
    toggleEdit: function () {
      this.$emit('toggleEdit')
    },
    getScrollBottom: function () {
      return this.$refs.messages.scrollHeight - this.$refs.messages.scrollTop
    },
    setScrollBottom: function (scrollPos) {
      this.$refs.messages.scrollTo(0, this.$refs.messages.scrollHeight - scrollPos)
    },
    saveScrollHeight: function () {
      this.scrollHeight = this.$refs.messages.scrollHeight
    },
    saveState: function () {
      const scrollHeight = this.getScrollBottom()
      const state = {
        scroll: scrollHeight
      }
      this._set(this.stateObjectKey, state)
    },
    restoreState: function () {
      const state = this._pop(this.stateObjectKey)
      if (state && state.scroll) {
        this.$nextTick(() => {
          setTimeout(() => {
            window.scrollTo({
              top: state.scroll,
              behavior: 'smooth'
            })
          }, 0)
        })
      } else {
        this.scrollToBottom()
      }
    },
    checkLoadMore: function () {
      if (this.isFirstLoad) {
        this.fetchMessages()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';

.messages {
  overflow-y: scroll;
  overflow-x: hidden;
  height: 100%;
  @include fix-ios-scrolling
}

.message {
  @include mobile {
    margin-bottom: 0 !important; //to override an inconsistent behaviour while testing on local/staging
  }
}

.loading {
  justify-content: center;
  font-size: 1.3rem;
  height: 2.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
